import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import EditorialSection from "@/components/EditorialSection";
import PlatformArchitecture from "@/components/PlatformArchitecture";
import ProductEvidence from "@/components/ProductEvidence";
import OperationalFlow from "@/components/OperationalFlow";
import DeploymentTopology from "@/components/DeploymentTopology";
import CTABand from "@/components/CTABand";

const chapters = [
  ["01", "LightningFlow Core", "Move and optimise network traffic across distributed environments while policy, health and performance remain visible."],
  ["02", "QuantumCloak", "Reduce unnecessary exposure and apply identity- and policy-aware access before protected infrastructure."],
  ["03", "Distributed nodes", "Extend the platform across cloud, on-premise, edge, branch and sovereign environments with one operating model."],
  ["04", "QuantumCubeDB", "Connect network activity, topology, health, events and policy into authoritative operational state."],
  ["05", "Sheila AI", "Explain what changed, why it matters and what to do next using governed operational context."],
  ["06", "Halo + endpoint", "Extend clear protection and connectivity state to people and their supported devices."],
  ["07", "FPGA acceleration", "Accelerate supported network workloads with visible health, measured performance and a software fallback."],
];

export default function Platform() {
  return (
    <>
      <SEO title="LightningFlow Platform — One Intelligent Network System" description="Explore how LightningFlow Core, QuantumCloak, QuantumCubeDB, Sheila AI and distributed nodes work as one operational platform." canonicalPath="/platform" image="/platform/lightningflow-command-center-v9.png" />

      <section className="lf-hero-editorial" data-testid="platform-hero">
        <div className="lf-container grid gap-12 py-16 md:py-24 lg:grid-cols-[1.05fr_.95fr] lg:items-center lg:py-28">
          <div>
            <p className="lf-hero-kicker">LightningFlow Platform</p>
            <h1 className="lf-hero-title max-w-6xl">One platform. <em>Multiple capabilities.</em></h1>
            <p className="lf-hero-summary">Bring your network together. Protect access, see your sites and services, and give your team the clarity to act.</p>
            <div className="lf-hero-actions">
              <Link to="/quantumcubedb" className="lf-btn-primary">Explore QuantumCubeDB <ArrowRight className="h-4 w-4" /></Link>
              <Link to="/security" className="lf-btn-secondary">Security &amp; Trust</Link>
            </div>
          </div>
          <figure className="lf-hero-evidence">
            <img src="/platform/lightningflow-command-center-v9-960.webp" alt="LightningFlow Command Centre showing live topology and operational intelligence" width="960" height="454" decoding="async" />
            <figcaption>Your network, in one view. Command Centre preview; interface may change.</figcaption>
          </figure>
        </div>
      </section>

      <EditorialSection number="01" label="System view" title="Your whole network. Working together." body="One connected view helps your team protect access, understand activity and manage services across every supported environment." data-testid="platform-architecture">
        <PlatformArchitecture />
      </EditorialSection>

      <EditorialSection number="02" label="Command environment" title="One place to see the network as a living system." body="The Command Centre brings topology, node intelligence, protected endpoints and current operational state into one interface.">
        <ProductEvidence src="/platform/lightningflow-command-center-v9.webp" alt="LightningFlow Command Centre with topology, node intelligence and endpoint protection state" caption="See topology, service health and network activity together. Command Centre preview; interface may change." meta="LightningFlow Command Centre" />
      </EditorialSection>

      <EditorialSection number="03" label="Connected capabilities" title="The capabilities that bring it together." dark>
        <div className="lf-editorial-list border-white/20">
          {chapters.map(([number, title, body]) => (
            <article key={title} className="border-white/20"><span className="lf-list-index text-cyan-200">{number}</span><h3>{title}</h3><p className="text-slate-300">{body}</p></article>
          ))}
        </div>
      </EditorialSection>

      <EditorialSection number="04" label="Operational progression" title="From activity to a governed decision." body="Operational intelligence should preserve the line between observation, explanation, recommendation and authorised action.">
        <OperationalFlow />
        <div className="mt-10 flex flex-wrap gap-3"><Link className="lf-btn-primary" to="/quantumcubedb">Explore QuantumCubeDB <ArrowRight className="h-4 w-4" /></Link><Link className="lf-btn-secondary" to="/sheila-ai">Explore Sheila AI</Link></div>
      </EditorialSection>

      <EditorialSection number="05" label="Deployment fabric" title="Distributed by design. Coherent by operation." body="The topology may span environments; policy and operational understanding should not fragment with it." data-testid="platform-surfaces">
        <DeploymentTopology />
      </EditorialSection>

      <CTABand />
    </>
  );
}
